interface AuthConfig {
  cognitoDomain: string;
  cognitoClientId: string;
  redirectUri: string;
}

let cachedConfig: AuthConfig | null = null;

/**
 * Fetches auth configuration from the backend /config endpoint.
 * Caches the result for subsequent calls.
 */
export async function getAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return cachedConfig;

  try {
    const res = await fetch('/config');
    if (!res.ok) throw new Error('Failed to fetch config');
    cachedConfig = await res.json();
    return cachedConfig!;
  } catch {
    // Fallback to env vars (for local dev) or window.location.origin
    cachedConfig = {
      cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN || '',
      cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
      redirectUri: import.meta.env.VITE_COGNITO_REDIRECT_URI || window.location.origin,
    };
    return cachedConfig;
  }
}

const TOKEN_KEYS = {
  accessToken: 'access_token',
  idToken: 'id_token',
} as const;

/**
 * Returns the Cognito Hosted UI login URL with implicit grant parameters.
 */
export function getLoginUrl(config: AuthConfig): string {
  if (!config.cognitoDomain || !config.cognitoClientId) {
    throw new Error('Cognito configuration is missing.');
  }

  const params = new URLSearchParams({
    response_type: 'token',
    client_id: config.cognitoClientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
  });

  return `${config.cognitoDomain}/login?${params.toString()}`;
}

/**
 * Redirects the browser to the Cognito Hosted UI login page.
 */
export async function redirectToLogin(): Promise<void> {
  const config = await getAuthConfig();
  window.location.href = getLoginUrl(config);
}

/**
 * Parses access_token and id_token from the URL hash fragment.
 */
export function parseTokensFromUrl(): { accessToken: string; idToken: string } | null {
  const hash = window.location.hash.substring(1);
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const accessToken = params.get('access_token');
  const idToken = params.get('id_token');

  if (!accessToken || !idToken) return null;
  return { accessToken, idToken };
}

/**
 * Stores tokens in sessionStorage.
 */
export function storeTokens(accessToken: string, idToken: string): void {
  sessionStorage.setItem(TOKEN_KEYS.accessToken, accessToken);
  sessionStorage.setItem(TOKEN_KEYS.idToken, idToken);
}

/**
 * Retrieves tokens from sessionStorage.
 */
export function getStoredTokens(): { accessToken: string; idToken: string } | null {
  const accessToken = sessionStorage.getItem(TOKEN_KEYS.accessToken);
  const idToken = sessionStorage.getItem(TOKEN_KEYS.idToken);
  if (!accessToken || !idToken) return null;
  return { accessToken, idToken };
}

/**
 * Removes tokens from sessionStorage (sign-out).
 */
export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEYS.accessToken);
  sessionStorage.removeItem(TOKEN_KEYS.idToken);
}

/**
 * Checks whether valid tokens exist in storage.
 */
export function isAuthenticated(): boolean {
  return getStoredTokens() !== null;
}
